'use client';

import React, { useState } from 'react';
import { format } from 'date-fns';
import { Calendar, Clock, Bell, Copy, Trash2, Edit3, Check, Plus, ExternalLink } from 'lucide-react';
import { cn } from '@/app/components/lib/utils';
export const CalendarEventDetails = ({
  event,
  onEdit,
  onDelete,
  className,
  slotProps
}) => {
  const [copied, setCopied] = useState(false);
  if (!event) {
    return <div className={cn('flex flex-col items-center justify-center p-8 text-center text-zinc-400 dark:text-zinc-500', className)}>
                <Calendar className="mb-2 size-8 stroke-1 text-zinc-300 dark:text-zinc-600" />
                <p className="text-sm font-medium">Select an event to view details</p>
            </div>;
  }
  const startDate = typeof event.start === 'string' ? new Date(event.start) : event.start;
  const endDate = typeof event.end === 'string' ? new Date(event.end) : event.end;
  const handleCopyLink = () => {
    const url = event.meetingUrl || window.location.href;
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const attendees = event.attendees || [{
    name: 'Sienna Hewitt',
    initials: 'SH'
  }, {
    name: 'Ammar Foley',
    initials: 'AF'
  }, {
    name: 'Pippa Wilkinson',
    initials: 'PW'
  }, {
    name: 'Olly Schroeder',
    initials: 'OS'
  }, {
    name: 'Mathilde Lewis',
    initials: 'ML'
  }, {
    name: 'Olivia Rhye',
    initials: 'OR'
  }];
  const detailsSlot = slotProps?.eventDetails;
  const dayViewSlot = slotProps?.dayView?.eventDetails;
  return <div {...dayViewSlot} {...detailsSlot?.root} className={cn('flex flex-col gap-5 p-6 text-zinc-900 dark:text-zinc-100', className, dayViewSlot?.className, detailsSlot?.root?.className)}>
            {/* Header section with title and utility action buttons */}
            <div className="flex items-start justify-between gap-3">
                <h3 className="text-base font-semibold leading-snug text-zinc-900 dark:text-zinc-100">{event.title}</h3>
                <div {...detailsSlot?.actionButtons} className={cn('flex items-center gap-0.5', detailsSlot?.actionButtons?.className)}>
                    <button type="button" onClick={handleCopyLink} title={copied ? 'Copied!' : 'Copy link'} aria-label="Copy link" {...detailsSlot?.copyButton} className={cn('flex size-7 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition-colors', detailsSlot?.copyButton?.className)}>
                        {copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
                    </button>
                    <button type="button" onClick={() => onEdit?.(event)} title="Edit event" aria-label="Edit event" {...detailsSlot?.editButton} className={cn('flex size-7 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition-colors', detailsSlot?.editButton?.className)}>
                        <Edit3 className="size-3.5" />
                    </button>
                    <button type="button" onClick={() => onDelete?.(event)} title="Delete event" aria-label="Delete event" {...detailsSlot?.deleteButton} className={cn('flex size-7 items-center justify-center rounded-lg text-zinc-500 hover:bg-red-50 hover:text-red-600 dark:text-zinc-400 dark:hover:bg-red-950/40 dark:hover:text-red-400 transition-colors', detailsSlot?.deleteButton?.className)}>
                        <Trash2 className="size-3.5" />
                    </button>
                </div>
            </div>

            {/* Meta rows (Date, Time, Reminder) */}
            <div className="flex flex-col gap-2 text-sm text-zinc-600 dark:text-zinc-400">
                <div className="flex items-center gap-2.5">
                    <Calendar className="size-4 shrink-0 text-zinc-400 dark:text-zinc-500" />
                    <span>{format(startDate, 'EEEE, MMM d, yyyy')}</span>
                </div>
                <div className="flex items-center gap-2.5">
                    <Clock className="size-4 shrink-0 text-zinc-400 dark:text-zinc-500" />
                    <span>
                        {format(startDate, 'h:mm a')} – {format(endDate, 'h:mm a')}
                    </span>
                </div>
                <div className="flex items-center gap-2.5">
                    <Bell className="size-4 shrink-0 text-zinc-400 dark:text-zinc-500" />
                    <span>10 min before</span>
                </div>
            </div>

            {/* Attendees section */}
            <div className="flex flex-col gap-2.5 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                <div className="flex items-center gap-2">
                    {/* Overlapping avatar stack */}
                    <div className="flex -space-x-2 overflow-hidden">
                        {attendees.slice(0, 5).map((att, i) => <div key={i} className="flex size-7 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/60 font-semibold text-xs text-purple-700 dark:text-purple-300 ring-2 ring-white dark:ring-zinc-900" title={att.name}>
                                {att.initials || att.name.slice(0, 2).toUpperCase()}
                            </div>)}
                        {attendees.length > 5 && <div className="flex size-7 items-center justify-center rounded-full bg-zinc-200 dark:bg-zinc-700 font-semibold text-xs text-zinc-700 dark:text-zinc-200 ring-2 ring-white dark:ring-zinc-900">
                                +{attendees.length - 5}
                            </div>}
                    </div>
                    <button type="button" className="flex size-7 items-center justify-center rounded-full border border-dashed border-zinc-300 dark:border-zinc-700 text-zinc-400 hover:border-zinc-400 hover:text-zinc-600 transition-colors" title="Add guest">
                        <Plus className="size-3.5" />
                    </button>
                </div>

                {/* Guest count & RSVP breakdown */}
                <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400 font-medium">
                    <span className="font-semibold text-zinc-900 dark:text-zinc-200">{attendees.length} guests</span>
                    <span className="h-3 w-px bg-zinc-200 dark:bg-zinc-700" />
                    <span>{Math.max(1, attendees.length - 1)} yes</span>
                    <span className="h-3 w-px bg-zinc-200 dark:bg-zinc-700" />
                    <span>1 awaiting</span>
                </div>
            </div>

            {/* About this event */}
            <div className="flex flex-col gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                <h4 className="text-xs font-semibold text-zinc-900 dark:text-zinc-100 uppercase tracking-wider">About this event</h4>
                <div className="text-xs leading-relaxed text-zinc-600 dark:text-zinc-400 space-y-2">
                    <p>{event.description || 'Topic: Product demo for the new dashboard and Q&A session.'}</p>
                    {event.meetingUrl && <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/60 p-2.5 border border-zinc-200/80 dark:border-zinc-700/60">
                            <span className="font-medium text-zinc-700 dark:text-zinc-300">Join Meeting:</span>{' '}
                            <a href={event.meetingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-purple-600 dark:text-purple-400 hover:underline break-all">
                                {event.meetingUrl}
                                <ExternalLink className="size-3 shrink-0" />
                            </a>
                            {event.meetingId && <div className="mt-1 text-zinc-500 dark:text-zinc-400">
                                    Meeting ID: <span className="font-mono text-zinc-700 dark:text-zinc-300">{event.meetingId}</span>
                                </div>}
                        </div>}
                </div>
            </div>
        </div>;
};